import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Search, Settings2, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => {
  const v = Number(n) || 0;
  const neg = v < 0;
  return `${neg ? '−' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function JobCostsList() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({ default_hourly_rate: 65, default_km_rate: 0.72 });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => { fetchJobs(); fetchSettings(); }, []);

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${API}/job-costs`, { credentials: 'include' });
      if (res.ok) setJobs(await res.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/job-costs/settings`, { credentials: 'include' });
      if (res.ok) setSettings(await res.json());
    } catch { /* ignore */ }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API}/job-costs/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          default_hourly_rate: parseFloat(settings.default_hourly_rate) || 0,
          default_km_rate: parseFloat(settings.default_km_rate) || 0,
        }),
      });
      if (res.ok) { toast.success('Settings saved'); setShowSettings(false); }
    } catch { toast.error('Failed to save'); }
    finally { setSavingSettings(false); }
  };

  const filtered = jobs.filter(j => {
    const matchSearch = !search
      || j.title?.toLowerCase().includes(search.toLowerCase())
      || j.estimate_number?.toLowerCase().includes(search.toLowerCase())
      || j.client?.name?.toLowerCase().includes(search.toLowerCase())
      || j.client?.company?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || j.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalRevenue = filtered.reduce((s, j) => s + (j.revenue || 0), 0);
  const totalCost = filtered.reduce((s, j) => s + (j.total_cost || 0), 0);
  const totalProfit = totalRevenue - totalCost;
  const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue * 100) : 0;

  return (
    <div className="animate-fade-in" data-testid="job-costs-list">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Job Costs
          </h1>
          <p className="text-sm text-[#6B7A73] mt-1">
            Track actuals, lead cost, and profit on every job
          </p>
        </div>
        <Button
          onClick={() => setShowSettings(true)}
          variant="outline"
          className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md"
          data-testid="job-costs-settings-btn"
        >
          <Settings2 className="w-4 h-4 mr-2" />
          Rates
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Jobs shown" value={filtered.length.toString()} mono={false} />
        <SummaryCard label="Revenue (accepted)" value={fmt(totalRevenue)} />
        <SummaryCard label="Total cost" value={fmt(totalCost)} tone="cost" />
        <SummaryCard
          label={totalProfit >= 0 ? `Profit · ${avgMargin.toFixed(1)}%` : `Loss · ${avgMargin.toFixed(1)}%`}
          value={fmt(totalProfit)}
          tone={totalProfit >= 0 ? 'profit' : 'loss'}
        />
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7A73]" />
          <Input
            data-testid="search-jobs-input"
            placeholder="Search by client, job, or estimate #..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-white border-[#E6EBE8] text-[#1A1D1C] rounded-md focus:border-[#2D6B68] focus:ring-1 focus:ring-[#2D6B68]"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white border-[#E6EBE8] rounded-md" data-testid="job-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E6EBE8] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <DollarSign className="w-12 h-12 text-[#E6EBE8] mx-auto mb-4" />
            <p className="text-[#6B7A73] text-sm">No jobs match your filters.</p>
          </div>
        ) : (
          <table className="w-full" data-testid="jobs-table">
            <thead>
              <tr className="bg-[#F9F9F8] border-b border-[#E6EBE8]">
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-left font-semibold">Job</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-right font-semibold">Revenue</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-right font-semibold hidden lg:table-cell">Labour</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-right font-semibold hidden lg:table-cell">Materials</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-right font-semibold hidden xl:table-cell">Lead</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-right font-semibold">Profit</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-right font-semibold">Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6EBE8]">
              {filtered.map(j => {
                const isProfit = j.profit >= 0;
                const clientDisplay = j.client?.company || j.client?.name || '—';
                return (
                  <tr
                    key={j.estimate_id}
                    className="hover:bg-[#F9F9F8] transition-colors cursor-pointer"
                    onClick={() => navigate(`/job-costs/${j.estimate_id}`)}
                    data-testid={`job-row-${j.estimate_id}`}
                  >
                    <td className="py-3 px-4">
                      <p className="text-sm font-bold text-[#1A1D1C]">{clientDisplay}</p>
                      <p className="text-xs text-[#6B7A73] mt-0.5">
                        {j.title}{' '}
                        <span style={{ fontFamily: "'IBM Plex Mono', monospace" }} className="text-[10px]">
                          · {j.estimate_number}
                        </span>
                      </p>
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-[#1A1D1C]"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {fmt(j.revenue)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-[#6B7A73] hidden lg:table-cell"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {fmt(j.labour)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-[#6B7A73] hidden lg:table-cell"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {fmt(j.materials)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-[#6B7A73] hidden xl:table-cell"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {fmt(j.lead_cost)}
                    </td>
                    <td className={`py-3 px-4 text-right text-sm font-semibold ${isProfit ? 'text-emerald-700' : 'text-red-600'}`}
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {fmt(j.profit)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${isProfit ? 'text-emerald-700' : 'text-red-600'}`}>
                        {isProfit ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {j.margin_pct.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Default Rates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#6B7A73] block mb-1">Default hourly rate ($/hr)</label>
              <Input
                type="number"
                step="0.01"
                value={settings.default_hourly_rate}
                onChange={e => setSettings({ ...settings, default_hourly_rate: e.target.value })}
                className="bg-white border-[#E6EBE8]"
                data-testid="default-hourly-rate-input"
              />
              <p className="text-xs text-[#6B7A73] mt-1">Used as the default when adding a new time entry. You can override per entry.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B7A73] block mb-1">Default mileage rate ($/km)</label>
              <Input
                type="number"
                step="0.01"
                value={settings.default_km_rate}
                onChange={e => setSettings({ ...settings, default_km_rate: e.target.value })}
                className="bg-white border-[#E6EBE8]"
                data-testid="default-km-rate-input"
              />
              <p className="text-xs text-[#6B7A73] mt-1">CRA's 2025 rate is $0.72/km for the first 5,000 km (Quebec).</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSettings(false)} className="border-[#E6EBE8]">Cancel</Button>
            <Button
              onClick={saveSettings}
              disabled={savingSettings}
              className="bg-[#2D6B68] text-white hover:bg-[#235552]"
              data-testid="save-rates-btn"
            >
              {savingSettings ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, tone = 'default', mono = true }) {
  const toneClass = tone === 'profit' ? 'text-emerald-700'
    : tone === 'loss' ? 'text-red-600'
    : tone === 'cost' ? 'text-[#6B7A73]'
    : 'text-[#1A1D1C]';
  return (
    <div className="bg-white border border-[#E6EBE8] rounded-md p-4">
      <p className="text-[10px] uppercase tracking-wider text-[#6B7A73] font-semibold">{label}</p>
      <p
        className={`text-xl font-bold mt-1 ${toneClass}`}
        style={mono ? { fontFamily: "'IBM Plex Mono', monospace" } : {}}
      >
        {value}
      </p>
    </div>
  );
}
